import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import Cropper from 'react-cropper';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import 'cropperjs/dist/cropper.css';
import './cropper-panel.scss';
import {Panel, PanelContent, PanelFooter, PanelFooterPrimaryDefaultAction, PanelFooterSecondaryDefaultAction, PanelHeader} from '@Components/panel';
import {getImageDimensions} from '@Utils/image.util';
import {getUniqueString} from '@Utils/string.util';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import type {BasePanel} from '@Components/panel/panel.types';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {setIsBackgroundRemoved, setShowLoading} from '@Panels/cropper-panel/cropper-panel-reducer';
import type {ImageItemSourceType} from '@Libraries/image-item.library';
import {getUrlForImageItemScreenTier, ScreenTier} from '@Libraries/image-item.library';
import type {CropperPanelHelperTooltip, CropData, CropperPanelApplyResponse} from '@Panels/cropper-panel/cropper-panel.types';
import {CropperDragModeOptions} from '@Panels/cropper-panel/cropper-panel.types';
import styles from './cropper-panel.module.scss';
import {CroppingPresets} from './components/cropping-presets';
import {CircularProgressLoader} from '../../circular-progress-loader';
import {CropperPanelOptions} from './components/cropper-panel-options';
import {useAppDispatch, useAppSelector} from '@/hooks';

export interface CropperPanelProps extends BasePanel {
  onApply(data: CropperPanelApplyResponse): void;

  disableAutoCloseOnApply?: boolean;

  getImgSrc?(): string;

  imageHashedFilename?: string;
  imageSource?: ImageItemSourceType;
  dataUrl?: string;
  showRemoveBackgroundOption?: boolean;
  aspectRatio?: number;
  isInitiallyBackgroundRemoved?: boolean;
  initialCropData?: null | CropData;
  isCropBoxResizable?: boolean;
  cropperDragMode?: Cropper.DragMode;
  helperTooltip?: CropperPanelHelperTooltip;
  showCroppingPresets?: boolean;
  headerTitle?: string;
  showCancelButton?: boolean;
  showCloseButton?: boolean;
  cancelButtonCtaText?: string;
  applyButtonCtaText?: string;
}

let cropper: Cropper | null = null;
let loadedImageWidth = 0;

const containerQuery: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 550,
  },
  large: {
    minWidth: 551,
  },
};

function CropperPanel({
  aspectRatio = NaN,
  showCroppingPresets = true,
  showCancelButton = false,
  isInitiallyBackgroundRemoved = false,
  showRemoveBackgroundOption = false,
  isCropBoxResizable = true,
  cropperDragMode = CropperDragModeOptions.CROP,
  headerTitle = window.i18next.t('pmwjs_crop_your_image'),
  applyButtonCtaText = window.i18next.t('pmwjs_apply'),
  cancelButtonCtaText = window.i18next.t('pmwjs_cancel'),
  ...props
}: CropperPanelProps): ReactElement {
  const showLoading = useAppSelector((state) => {
    return state.cropperPanel.showLoading;
  });
  const isBackgroundRemoved = useAppSelector((state) => {
    return state.cropperPanel.isBackgroundRemoved;
  });
  const panelMessageFromState = useAppSelector((state) => {
    return state.cropperPanel.panelMessage;
  });
  const [imageOriginalRatio, setImageOriginalRatio] = useState(0);
  const [imgSrc, setImgSrc] = useState('');
  const cropContentElement = useRef<HTMLDivElement>(null);
  const [cropperDimensions, setCropperDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [currentAspectRatio, setCurrentAspectRatio] = useState(aspectRatio);
  const [showHelperTooltip, setShowHelperTooltip] = useState(false);
  const imageElement = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);
  const panelId = useRef(props.panelId ?? getUniqueString());
  const onClose = useDefaultPanelClose(panelId.current);
  const dispatch = useAppDispatch();
  const DEFAULT_TOOLTIP_SHOW_AFTER_MS = 0;

  useEffect(() => {
    dispatch(setShowLoading(true));
    dispatch(setIsBackgroundRemoved(isInitiallyBackgroundRemoved));
    void initImgSrc();

    if (!cropContentElement.current) {
      return;
    }

    const positionInfo = cropContentElement.current.getBoundingClientRect();
    setCropperDimensions({
      width: positionInfo.width,
      height: positionInfo.height,
    });
  }, []);

  useEffect(() => {
    if (imageElement.current) {
      imageElement.current.addEventListener('ready', hideLoading);
    }
    return () => {
      if (imageElement.current) {
        imageElement.current.removeEventListener('ready', hideLoading);
      }
    };
  }, [props]);

  const initImgSrc = async (): Promise<void> => {
    const src = getImgSrcToLoad(isInitiallyBackgroundRemoved);
    const [imageWidth, imageHeight] = await getImageDimensions(src);
    loadedImageWidth = imageWidth;
    setImageOriginalRatio(imageWidth / imageHeight);
    setCurrentAspectRatio(aspectRatio);
    setImgSrc(src);
  };

  const getImgSrcToLoad = (backgroundRemovedVersion: boolean): string => {
    if (props.getImgSrc !== undefined) {
      return props.getImgSrc();
    }

    if (props.imageHashedFilename && props.imageSource) {
      return getUrlForImageItemScreenTier(props.imageHashedFilename, props.imageSource, ScreenTier.BIG, backgroundRemovedVersion);
    }

    if (props.dataUrl) {
      return props.dataUrl;
    }

    throw new Error('Neither hashedFilename+Extension nor dataUrl is present');
  };

  const getContent = (): ReactElement => {
    return (
      <PanelContent className={`${styles.content} flexbox flex-items-center`}>
        {getCropperOptions()}
        <div className={`${styles.cropContent} flex-1 flex-center`}>
          <div className={`${showLoading ? styles.containerHidden : ''} ${styles.cropperContainer} flex-center`} ref={cropContentElement}>
            {imgSrc ? (
              <Cropper
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  ...cropperDimensions,
                }}
                initialAspectRatio={aspectRatio}
                src={imgSrc}
                viewMode={1}
                minCropBoxHeight={10}
                minCropBoxWidth={10}
                background={false}
                cropBoxResizable={isCropBoxResizable}
                dragMode={cropperDragMode}
                responsive
                data={getInitialCropData()}
                autoCropArea={1}
                checkOrientation={false}
                onInitialized={(instance) => {
                  cropper = instance;
                  dispatch(setShowLoading(false));
                  if (props.helperTooltip) {
                    triggerHelperTooltip();
                  }
                }}
                checkCrossOrigin={false}
                guides
              />
            ) : null}
          </div>
          {showLoading ? (
            <div className={styles.loadingOverlay}>
              <CircularProgressLoader />
            </div>
          ) : null}
        </div>
      </PanelContent>
    );
  };

  const getCropperOptions = (): ReactElement | undefined => {
    if (showCroppingPresets) {
      return (
        <CropperPanelOptions isSmallScreen={params.small} imgHashedFilename={props.imageHashedFilename} showRemoveBackgroundOption={showRemoveBackgroundOption} imgSrc={imgSrc}>
          <CroppingPresets activePreset={currentAspectRatio} onPresetSelected={onPresetSelected} imageOriginalRatio={imageOriginalRatio} />
        </CropperPanelOptions>
      );
    }
    return undefined;
  };

  useEffect(() => {
    if (cropper) {
      const cropData = cropper.getData();
      cropper.replace(getImgSrcToLoad(isBackgroundRemoved));
      if (cropData) {
        cropper.setData(cropData);
      }
    }
  }, [isBackgroundRemoved]);

  const onPresetSelected = (val: number): void => {
    if (!cropper) {
      return;
    }
    setCurrentAspectRatio(val);
    cropper.setAspectRatio(val);
  };

  const getFooterPrimaryActions = (): ReactElement[] => {
    return [<PanelFooterPrimaryDefaultAction key="btn-crop" customClasses="footer-crop-btn" disabled={false} text={applyButtonCtaText} onClick={onApply} />];
  };

  const getFooterSecondaryActions = (): ReactElement[] => {
    const buttons: ReactElement[] = [];
    if (showCancelButton) {
      buttons.push(<PanelFooterSecondaryDefaultAction key="btn-cancel" customClasses="footer-adjust-btn" disabled={false} text={cancelButtonCtaText} onClick={onDontCrop} />);
    }

    return buttons;
  };

  const onApply = (): void => {
    if (!cropper) {
      return;
    }

    const cropData = getCropData();
    if (!cropData) {
      return;
    }

    if (cropData.width === 0 || cropData.height === 0) {
      return;
    }

    props.onApply({
      cropData,
      isBackgroundRemoved,
    });
  };

  const getCropData = (): CropData | undefined => {
    if (!cropper) {
      return undefined;
    }
    const cropData = cropper.getData();
    const imageData = cropper.getImageData();

    return {
      x: cropData.x,
      y: cropData.y,
      width: cropData.width,
      height: cropData.height,
      imageWidth: imageData.naturalWidth,
      imageHeight: imageData.naturalHeight,
    };
  };

  const onDontCrop = (): void => {
    onClose();
  };

  const onPanelClose = (): void => {
    if (cropper) {
      cropper.destroy();
    }
    onClose();
  };

  /**
   * Get initial accordingly scaled crop data
   */
  const getInitialCropData = (): Cropper.SetDataOptions | undefined => {
    if (!props.initialCropData) {
      return undefined;
    }
    const ratio = loadedImageWidth / props.initialCropData.imageWidth;

    return {
      x: props.initialCropData.x * ratio,
      y: props.initialCropData.y * ratio,
      width: props.initialCropData.width * ratio,
      height: props.initialCropData.height * ratio,
    };
  };

  const triggerHelperTooltip = (): void => {
    setTimeout(() => {
      setShowHelperTooltip(true);

      if (props.helperTooltip?.hideTooltipAfterSeconds) {
        setTimeout(() => {
          setShowHelperTooltip(false);
        }, props.helperTooltip?.hideTooltipAfterSeconds);
      }
    }, props.helperTooltip?.showTooltipAfterSeconds ?? DEFAULT_TOOLTIP_SHOW_AFTER_MS);
  };

  const getPanelHeader = (): ReactElement => {
    if (!props.helperTooltip) {
      return <PanelHeader title={headerTitle} onBackButton={onDontCrop} onClose={onPanelClose} showCloseButton={props.showCloseButton} />;
    }

    return (
      <OverlayTrigger placement="bottom" show={showHelperTooltip} overlay={<Tooltip className={styles.toolTip}>{props.helperTooltip.message}</Tooltip>} offset={[0, 5]}>
        <div>
          <PanelHeader title={headerTitle} onBackButton={onDontCrop} onClose={onPanelClose} showCloseButton={props.showCloseButton} />
        </div>
      </OverlayTrigger>
    );
  };

  const hideLoading = (): void => {
    dispatch(setShowLoading(false));
  };

  return (
    <Panel
      ref={containerRef}
      className={`crop-panel ${params.small ? styles.small : ''}`}
      panelHeader={getPanelHeader()}
      panelContent={getContent()}
      panelFooter={
        <PanelFooter fullWidthPrimaryActions={params.small} border={!params.small} primaryActions={getFooterPrimaryActions()} secondaryActions={getFooterSecondaryActions()} />
      }
      {...(panelMessageFromState && {panelMessage: panelMessageFromState})}
    />
  );
}

export default CropperPanel;
